import React, { useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import useInView from '../hooks/useInView';
import { RENDER_LIST } from '../data/renders';

const AppShowcaseSection = () => {
  const scrollRef = useRef(null);
  const [ref, inView] = useInView(0.05);
  const scroll = (dir) => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: dir * 300, behavior: 'smooth' });
  };

  return (
    <section data-testid="app-showcase-section" className="relative py-24 overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[450px] bg-purple-600/10 rounded-full blur-[180px] pointer-events-none" />
      <div className="absolute top-10 right-0 w-72 h-72 bg-fuchsia-500/8 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          ref={ref}
          className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-purple-400 mb-3 font-outfit">Inside the App</p>
          <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
            <div>
              <h2 className="font-outfit font-bold text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight mb-2">Inside ROMIND KIDS.</h2>
              <p className="text-white/50 text-base max-w-xl">
                Real renders from inside the platform — Profile, Story Portal, RONI, Chat, and Times Cards. Visuals are in active development.
              </p>
            </div>
            <div className="hidden sm:flex gap-2 flex-shrink-0">
              <button
                onClick={() => scroll(-1)}
                data-testid="showcase-scroll-left"
                aria-label="Scroll left"
                className="w-10 h-10 rounded-full glass-card flex items-center justify-center hover:border-purple-500/30 transition-all"
              >
                <ChevronLeft size={17} className="text-white/65" />
              </button>
              <button
                onClick={() => scroll(1)}
                data-testid="showcase-scroll-right"
                aria-label="Scroll right"
                className="w-10 h-10 rounded-full glass-card flex items-center justify-center hover:border-purple-500/30 transition-all"
              >
                <ChevronRight size={17} className="text-white/65" />
              </button>
            </div>
          </div>
        </div>

        <div
          ref={scrollRef}
          className="flex gap-6 sm:gap-8 overflow-x-auto pb-8 snap-x snap-mandatory -mx-4 px-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {RENDER_LIST.map((s) => (
            <div
              key={s.id}
              data-testid={`app-screen-${s.id}`}
              className="flex-shrink-0 snap-center flex flex-col items-center"
            >
              <div className="relative" style={{ width: 260 }}>
                <div className={`absolute -inset-5 bg-gradient-to-br ${s.glow} rounded-[56px] blur-[48px] pointer-events-none`} />
                <img
                  src={s.src}
                  alt={`ROMIND KIDS — ${s.title}`}
                  className="relative w-full h-auto select-none drop-shadow-[0_18px_50px_rgba(0,0,0,0.55)]"
                  draggable="false"
                  loading="lazy"
                />
              </div>
              <p className="text-center text-sm text-white/75 mt-4 font-outfit font-semibold">{s.title}</p>
              <p className="text-center text-[11px] text-white/40 mt-1 max-w-[240px]">{s.caption}</p>
            </div>
          ))}
        </div>

        <p className="text-[11px] text-white/30 mt-2 text-center sm:text-left">
          Concept previews · Final UI may evolve as the platform approaches launch.
        </p>
      </div>
    </section>
  );
};

export default AppShowcaseSection;
